<template>
    <v-card-text>
        <!-- Cifras -->
        <v-row dense class="mb-2">
            <v-col v-for="c in cifras" :key="c.clave" cols="6" sm="4" md="2">
                <v-card outlined class="cifra" :class="'cifra--' + c.color">
                    <div class="cifra__valor">{{ c.valor }}</div>
                    <div class="cifra__texto">{{ c.texto }}</div>
                </v-card>
            </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mb-3" style="gap: 6px 8px">
            <v-chip small label :color="cupo.pausado ? 'warning' : 'success'" text-color="white">
                <v-icon small left>{{ cupo.pausado ? 'pause_circle' : 'play_circle' }}</v-icon>{{ cupo.pausado ? 'Envío pausado' : 'Envío activo' }}
            </v-chip>
            <v-chip small label :color="cupo.habilitado ? 'primary' : 'grey'" text-color="white" :title="cupo.habilitado ? '' : 'Configure MAIL_* y CORREO_HABILITADO en el servidor'">
                <v-icon small left>{{ cupo.habilitado ? 'mark_email_read' : 'unsubscribe' }}</v-icon>{{ cupo.habilitado ? 'Correo saliente configurado' : 'Correo saliente no configurado' }}
            </v-chip>
            <v-chip small label outlined color="primary">
                <v-icon small left>{{ automatico ? 'bolt' : 'flash_off' }}</v-icon>Automático al importar: {{ automatico ? 'sí' : 'no' }}
            </v-chip>
            <span class="text-caption grey--text text--darken-1">El cron envía cada 10 minutos, en orden de llegada, hasta el límite diario.</span>
            <v-spacer></v-spacer>
            <v-btn color="info" small outlined :loading="cargando" @click="cargar"><v-icon small>refresh</v-icon></v-btn>
            <v-btn v-if="permisos.editar" small outlined :color="cupo.pausado ? 'success' : 'warning'" :loading="accionando" @click="pausar(!cupo.pausado)">
                <v-icon small left>{{ cupo.pausado ? 'play_arrow' : 'pause' }}</v-icon>{{ cupo.pausado ? 'Reanudar' : 'Pausar' }}
            </v-btn>
            <v-btn v-if="permisos.editar" small outlined color="error" :disabled="!porEstado.error" :loading="accionando" @click="reintentar">
                <v-icon small left>replay</v-icon>Reintentar errores
            </v-btn>
            <v-btn v-if="permisos.editar" small outlined color="primary" :disabled="!porEstado.pendiente || cupo.pausado" :loading="enviando" @click="enviarAhora">
                <v-icon small left>send</v-icon>Enviar ahora
            </v-btn>
            <v-btn v-if="permisos.crear" small depressed color="success" @click="abrirEncolar">
                <v-icon small left>playlist_add</v-icon>Encolar certificados de una importación
            </v-btn>
        </div>

        <!-- Filtros -->
        <v-row dense align="center" class="mb-1">
            <v-col cols="12" sm="4" md="3">
                <v-select v-model="filtros.estado" :items="opcionesEstado" label="Estado" outlined dense hide-details clearable @change="recargar"/>
            </v-col>
            <v-col cols="12" sm="8" md="5">
                <v-text-field v-model="filtros.buscar" label="Buscar por correo, participante, asunto o registro" outlined dense hide-details clearable
                              prepend-inner-icon="search" @keyup.enter="recargar" @click:clear="filtros.buscar = ''; recargar()"/>
            </v-col>
            <v-col cols="12" md="4" class="text-md-right text-caption grey--text text--darken-1">
                {{ total }} correos en la cola{{ filtros.estado || filtros.buscar ? ' (filtrados)' : '' }}
            </v-col>
        </v-row>

        <v-data-table
            dense
            :headers="encabezados"
            :items="filas"
            :loading="cargando"
            item-key="correo_id"
            :server-items-length="total"
            :options.sync="opciones"
            :footer-props="pieTabla"
            must-sort
        >
            <template v-slot:[`item.persona`]="{ item }">
                <div>{{ item.persona || '—' }}</div>
                <div class="text-caption grey--text">{{ item.para }}</div>
            </template>
            <template v-slot:[`item.curso`]="{ item }">
                <div class="text-truncate" style="max-width: 260px" :title="item.curso">{{ item.curso || item.plantilla_clave }}</div>
                <div class="text-caption grey--text" v-if="item.registro">Registro {{ item.registro }}<span v-if="item.importacion"> · Importación {{ item.importacion }}</span></div>
            </template>
            <template v-slot:[`item.estado`]="{ item }">
                <v-chip x-small label :color="colorEstado(item.estado)" text-color="white">{{ textoEstado(item.estado) }}</v-chip>
                <v-tooltip bottom v-if="item.error" max-width="420">
                    <template v-slot:activator="{ on }">
                        <v-icon small color="error" class="ml-1" v-on="on">info</v-icon>
                    </template>
                    <span>{{ item.error }}</span>
                </v-tooltip>
            </template>
            <template v-slot:[`item.enviado_en`]="{ item }">
                <span v-if="item.enviado_en">{{ item.enviado_en | fechaESPHora }}</span>
                <span v-else-if="item.programado_para" class="grey--text" :title="'Reintento programado'">desde {{ item.programado_para | fechaESPHora }}</span>
                <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:[`item.fecha_crea`]="{ item }">{{ item.fecha_crea | fechaESPHora }}</template>
            <template v-slot:no-data>
                <v-alert dense type="info" outlined class="text-center mt-3">
                    No hay correos en la cola. Se encolan al procesar una importación (si el envío automático está activo) o con el botón "Encolar certificados de una importación".
                </v-alert>
            </template>
        </v-data-table>

        <!-- Encolar importación -->
        <v-dialog v-model="dialogoEncolar" max-width="620" persistent>
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">Encolar certificados de una importación</v-card-title>
                <v-card-text>
                    <p class="text-body-2">
                        Se encola el correo "certificado disponible" para cada curso aprobado de la importación cuyo participante tenga
                        correo, y se crea el aviso en la campana. Los que ya estaban en la cola no se repiten.
                    </p>
                    <v-autocomplete
                        v-model="importacionElegida"
                        :items="importaciones"
                        :loading="cargandoImportaciones"
                        item-text="texto"
                        item-value="importacion_id"
                        label="Importación procesada"
                        outlined dense clearable
                        no-data-text="No hay importaciones procesadas"
                        prepend-inner-icon="file_upload"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogoEncolar = false">Cancelar</v-btn>
                    <v-btn color="success" depressed :disabled="!importacionElegida" :loading="accionando" @click="encolar">
                        <v-icon small left>playlist_add</v-icon> Encolar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card-text>
</template>

<script>
const TEXTO_ESTADO = {pendiente: "Pendiente", enviado: "Enviado", error: "Error", omitido: "Omitido"};
const COLOR_ESTADO = {pendiente: "blue-grey", enviado: "success", error: "error", omitido: "grey"};

/** Pestaña Envíos: cola de correos con cifras, filtros, paginación en el servidor y acciones. */
export default {
    name: "NotificacionesEnvios",
    data() {
        return {
            cargando: false,
            cargado: false,
            accionando: false,
            enviando: false,
            cargandoImportaciones: false,
            filas: [],
            total: 0,
            porEstado: {pendiente: 0, enviado: 0, error: 0, omitido: 0},
            cupo: {limite_diario: 0, enviados_24h: 0, restante: 0, enviados_hoy: 0, pausado: false, habilitado: true},
            automatico: false,
            permisos: {crear: false, editar: false, eliminar: false},
            filtros: {estado: null, buscar: ""},
            opciones: {page: 1, itemsPerPage: 30, sortBy: ["correo_id"], sortDesc: [true]},
            dialogoEncolar: false,
            importaciones: [],
            importacionElegida: null,
            encabezados: [
                {text: "#", value: "correo_id", align: "end", width: 70},
                {text: "Participante / correo", value: "persona", sortable: false},
                {text: "Curso", value: "curso", sortable: false},
                {text: "Estado", value: "estado", sortable: false},
                {text: "Intentos", value: "intentos", sortable: false, align: "end"},
                {text: "Enviado", value: "enviado_en", sortable: false},
                {text: "Encolado", value: "fecha_crea", sortable: false},
            ],
            opcionesEstado: Object.keys(TEXTO_ESTADO).map(v => ({text: TEXTO_ESTADO[v], value: v})),
        }
    },
    computed: {
        cifras() {
            return [
                {clave: "pendiente", texto: "Pendientes", valor: this.porEstado.pendiente, color: "pendiente"},
                {clave: "enviado", texto: "Enviados", valor: this.porEstado.enviado, color: "enviado"},
                {clave: "error", texto: "Con error", valor: this.porEstado.error, color: "error"},
                {clave: "omitido", texto: "Omitidos", valor: this.porEstado.omitido, color: "omitido"},
                {clave: "hoy", texto: "Enviados hoy", valor: this.cupo.enviados_hoy, color: "hoy"},
                {clave: "cupo", texto: `Cupo restante de ${this.cupo.limite_diario}`, valor: this.cupo.restante, color: "cupo"},
            ];
        },
        pieTabla() {
            return {...this.$store.state.footerPropsTable, "items-per-page-options": [15, 30, 50, 100, 200]};
        },
    },
    watch: {
        opciones: {
            handler() {
                this.cargar();
            },
            deep: true,
        },
    },
    methods: {
        textoEstado(estado) {
            return TEXTO_ESTADO[estado] || estado;
        },
        colorEstado(estado) {
            return COLOR_ESTADO[estado] || "grey";
        },
        recargar() {
            if (this.opciones.page !== 1) {
                this.opciones.page = 1; // El watch recarga.
            } else {
                this.cargar();
            }
        },
        cargar() {
            this.cargado = true;
            this.cargando = true;
            const params = {
                pagina: this.opciones.page,
                por_pagina: this.opciones.itemsPerPage > 0 ? this.opciones.itemsPerPage : 200,
            };
            if (this.filtros.estado) params.estado = this.filtros.estado;
            if ((this.filtros.buscar || "").trim()) params.buscar = this.filtros.buscar.trim();

            window.axios.get("/correos/envios", {params}).then(response => {
                if (response.data.success) {
                    const d = response.data.data;
                    this.filas = d.filas;
                    this.total = d.total;
                    this.porEstado = d.por_estado;
                    this.cupo = d.cupo;
                    this.automatico = d.automatico;
                    this.permisos = d.permisos;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudieron cargar los envíos."))
                .finally(() => {
                    this.cargando = false;
                });
        },
        accion(ruta, datos, exito) {
            this.accionando = true;
            return window.axios.post(ruta, datos).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message, {timeout: 8000});
                    if (exito) exito(response.data.data);
                    this.cargar();
                } else {
                    this.$toast.warning(response.data.message, {timeout: 10000});
                    this.cargar();
                }
            })
                .catch(error => this.$toast.error(this.mensajeError(error, "No se pudo completar la acción."), {timeout: 8000}))
                .finally(() => {
                    this.accionando = false;
                });
        },
        pausar(pausado) {
            this.accion("/correos/pausar", {pausado});
        },
        reintentar() {
            this.accion("/correos/reintentar", {});
        },
        enviarAhora() {
            this.enviando = true;
            this.accion("/correos/enviar_ahora", {}).finally(() => {
                this.enviando = false;
            });
        },
        abrirEncolar() {
            this.importacionElegida = null;
            this.dialogoEncolar = true;
            this.cargandoImportaciones = true;
            window.axios.get("/correos/importaciones").then(response => {
                if (response.data.success) {
                    this.importaciones = response.data.data;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudieron cargar las importaciones."))
                .finally(() => {
                    this.cargandoImportaciones = false;
                });
        },
        encolar() {
            this.accion("/correos/encolar_importacion", {importacion_id: this.importacionElegida}, () => {
                this.dialogoEncolar = false;
            });
        },
        mensajeError(error, defecto) {
            return (error.response && error.response.data && error.response.data.message) || defecto;
        },
    },
    mounted() {
        // La tabla emite sus opciones al montarse y el watch carga; si no lo hizo, se carga aquí.
        this.$nextTick(() => {
            if (!this.cargado) this.cargar();
        });
    },
}
</script>

<style scoped>
.cifra {
    padding: 10px 14px;
    border-left: 4px solid #90a4ae;
}

.cifra__valor {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
    color: #1b2340;
}

.cifra__texto {
    font-size: .75rem;
    color: #5b6380;
}

.cifra--pendiente { border-left-color: #607d8b; }
.cifra--enviado { border-left-color: #4caf50; }
.cifra--error { border-left-color: #e53935; }
.cifra--omitido { border-left-color: #9e9e9e; }
.cifra--hoy { border-left-color: #203D89; }
.cifra--cupo { border-left-color: #D29E4A; }
</style>
